import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  Branch,
  BranchCreatedEnvelope,
  BranchDeletedEnvelope,
  BranchQuery,
  BranchUpdatedEnvelope,
  BranchesQueryBuilder,
  BulkUpdateBranchTagsApplicationErrors,
  BulkUpdateBranchTagsByFilterApplicationErrors,
  BulkUpdateBranchTagsByFilterOptions,
  BulkUpdateBranchTagsByFilterResponse,
  BulkUpdateBranchTagsOptions,
  BulkUpdateBranchTagsResponse,
  CreateBranchApplicationErrors,
  DeleteBranchApplicationErrors,
  GetDefaultBranchResponse,
  QueryBranchesResponse,
  SetDefaultBranchResponse,
  UpdateBranch,
  UpdateBranchApplicationErrors,
  bulkUpdateBranchTags as universalBulkUpdateBranchTags,
  bulkUpdateBranchTagsByFilter as universalBulkUpdateBranchTagsByFilter,
  createBranch as universalCreateBranch,
  deleteBranch as universalDeleteBranch,
  getBranch as universalGetBranch,
  getDefaultBranch as universalGetDefaultBranch,
  queryBranches as universalQueryBranches,
  setDefaultBranch as universalSetDefaultBranch,
  typedQueryBranches as universalTypedQueryBranches,
  updateBranch as universalUpdateBranch,
} from './editor-branches-v1-branch-branches.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/editor-branches' };

export function createBranch(httpClient: HttpClient): CreateBranchSignature {
  return (branch: Branch) =>
    universalCreateBranch(
      branch,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateBranchSignature {
  /**
   * Creates a branch.
   *
   * If a source branch isn't provided, the branch will include a blank website.
   *
   * > **Important:** Align the `sourceType` field with its corresponding source properties:
   * > - When `sourceType` is `SOURCE_BRANCH`, provide `sourceBranchProperties`.
   * > - When `sourceType` is `SOURCE_TEMPLATE`, provide `sourceTemplateProperties`.
   * >
   * > Using `UNKNOWN_SOURCE_TYPE` or mismatched properties results in an `ILLEGAL_SOURCE_TYPE` error.
   * @param - Branch to create.
   * @returns Created branch.
   */
  (branch: Branch): Promise<
    NonNullablePaths<
      Branch,
      | `sourceBranchProperties.branchId`
      | `sourceTemplateProperties.templateId`
      | `type`
      | `sourceType`
      | `editorType.editorTypeOptions`
      | `tags.tags.tagIds`,
      4
    > & {
      __applicationErrorsType?: CreateBranchApplicationErrors;
    }
  >;
}

export function getBranch(httpClient: HttpClient): GetBranchSignature {
  return (branchId: string) =>
    universalGetBranch(
      branchId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetBranchSignature {
  /**
   * Retrieves a branch.
   * @param - ID of the branch to retrieve.
   * @returns Requested branch.
   */
  (branchId: string): Promise<
    NonNullablePaths<
      Branch,
      | `sourceBranchProperties.branchId`
      | `sourceTemplateProperties.templateId`
      | `type`
      | `sourceType`
      | `editorType.editorTypeOptions`
      | `tags.tags.tagIds`,
      4
    >
  >;
}

export function updateBranch(httpClient: HttpClient): UpdateBranchSignature {
  return (_id: string, branch: NonNullablePaths<UpdateBranch, `revision`, 2>) =>
    universalUpdateBranch(
      _id,
      branch,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateBranchSignature {
  /**
   * Updates a branch.
   *
   * Each time the branch is updated, `revision` increments by 1. The current `revision` must be passed when updating the branch. This ensures you're working with the latest branch and prevents unintended overwrites.
   *
   * Note that `ORIGINAL_BRANCH` types can't be updated.
   * @param - Branch ID.
   * @returns Updated branch.
   */
  (_id: string, branch: NonNullablePaths<UpdateBranch, `revision`, 2>): Promise<
    NonNullablePaths<
      Branch,
      | `sourceBranchProperties.branchId`
      | `sourceTemplateProperties.templateId`
      | `type`
      | `sourceType`
      | `editorType.editorTypeOptions`
      | `tags.tags.tagIds`,
      4
    > & {
      __applicationErrorsType?: UpdateBranchApplicationErrors;
    }
  >;
}

export function deleteBranch(httpClient: HttpClient): DeleteBranchSignature {
  return (branchId: string) =>
    universalDeleteBranch(
      branchId,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteBranchSignature {
  /**
   * Permanently deletes a branch.
   * @param - ID of the branch to delete.
   */
  (branchId: string): Promise<
    void & {
      __applicationErrorsType?: DeleteBranchApplicationErrors;
    }
  >;
}

export function queryBranches(httpClient: HttpClient): QueryBranchesSignature {
  return () =>
    universalQueryBranches(
      // @ts-ignore
      { httpClient }
    );
}

interface QueryBranchesSignature {
  /**
   * Retrieves a list of branches, given the provided paging, filtering, and sorting.
   *
   *
   * Query Branches runs with these defaults, which you can override:
   *
   * - `updatedDate` is sorted in `DESC` order
   * - `paging.limit` is `50`
   * - `paging.offset` is `0`
   *
   * To learn how to query branches, see [API Query Language](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language).
   */
  (): BranchesQueryBuilder;
}

export function typedQueryBranches(
  httpClient: HttpClient
): TypedQueryBranchesSignature {
  return (query: BranchQuery) =>
    universalTypedQueryBranches(
      query,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryBranchesSignature {
  /** */
  (query: BranchQuery): Promise<
    NonNullablePaths<
      QueryBranchesResponse,
      | `branches`
      | `branches.${number}.sourceBranchProperties.branchId`
      | `branches.${number}.sourceTemplateProperties.templateId`
      | `branches.${number}.type`
      | `branches.${number}.sourceType`
      | `branches.${number}.editorType.editorTypeOptions`,
      5
    >
  >;
}

export function setDefaultBranch(
  httpClient: HttpClient
): SetDefaultBranchSignature {
  return (branchId: string) =>
    universalSetDefaultBranch(
      branchId,
      // @ts-ignore
      { httpClient }
    );
}

interface SetDefaultBranchSignature {
  /**
   * Sets the specified branch as the default.
   *
   * The default branch is used for API operations when no branch ID is specified.
   * @param - Branch ID.
   */
  (branchId: string): Promise<
    NonNullablePaths<
      SetDefaultBranchResponse,
      | `branch.sourceBranchProperties.branchId`
      | `branch.sourceTemplateProperties.templateId`
      | `branch.type`
      | `branch.sourceType`
      | `branch.editorType.editorTypeOptions`
      | `branch.tags.tags.tagIds`,
      5
    >
  >;
}

export function getDefaultBranch(
  httpClient: HttpClient
): GetDefaultBranchSignature {
  return () =>
    universalGetDefaultBranch(
      // @ts-ignore
      { httpClient }
    );
}

interface GetDefaultBranchSignature {
  /**
   * Retrieves the default branch.
   */
  (): Promise<
    NonNullablePaths<
      GetDefaultBranchResponse,
      | `branch.sourceBranchProperties.branchId`
      | `branch.sourceTemplateProperties.templateId`
      | `branch.type`
      | `branch.sourceType`
      | `branch.editorType.editorTypeOptions`
      | `branch.tags.tags.tagIds`,
      5
    >
  >;
}

export function bulkUpdateBranchTags(
  httpClient: HttpClient
): BulkUpdateBranchTagsSignature {
  return (branchIds: string[], options?: BulkUpdateBranchTagsOptions) =>
    universalBulkUpdateBranchTags(
      branchIds,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdateBranchTagsSignature {
  /**
   * Updates tags on multiple branches.
   *
   * Specify which tags to assign and which tags to unassign. A tag that appears in both the list to assign and the list to unassign will be assigned.
   *
   * To update a single branch's tags, call [Update Branch](https://dev.wix.com/docs/api-reference/business-management/branches/update-branch).
   * @param - IDs of the branches to update.
   */
  (branchIds: string[], options?: BulkUpdateBranchTagsOptions): Promise<
    NonNullablePaths<
      BulkUpdateBranchTagsResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    > & {
      __applicationErrorsType?: BulkUpdateBranchTagsApplicationErrors;
    }
  >;
}

export function bulkUpdateBranchTagsByFilter(
  httpClient: HttpClient
): BulkUpdateBranchTagsByFilterSignature {
  return (
    filter: Record<string, any>,
    options?: BulkUpdateBranchTagsByFilterOptions
  ) =>
    universalBulkUpdateBranchTagsByFilter(
      filter,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdateBranchTagsByFilterSignature {
  /**
   * Asynchronously updates tags on multiple branches that match the provided filter criteria.
   *
   * Specify which tags to assign and which tags to unassign. A tag that appears both the list to assign and the list to unassign tags, will be assigned.
   *
   * To check the status of the bulk update, pass the returned `jobId` to [Get Async Job](https://dev.wix.com/docs/api-reference/business-management/async-job/get-async-job).
   *
   * To update a single branch's tags, call [Update Branch](https://dev.wix.com/docs/api-reference/business-management/branches/update-branch).
   * @param - Filter to select which branches to update. See [API Query Language](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language) for more information.
   */
  (
    filter: Record<string, any>,
    options?: BulkUpdateBranchTagsByFilterOptions
  ): Promise<
    NonNullablePaths<BulkUpdateBranchTagsByFilterResponse, `jobId`, 2> & {
      __applicationErrorsType?: BulkUpdateBranchTagsByFilterApplicationErrors;
    }
  >;
}

export const onBranchCreated = EventDefinition(
  'wix.editor.branches.v1.branch_created',
  true,
  (event: BranchCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<BranchCreatedEnvelope>();
export const onBranchDeleted = EventDefinition(
  'wix.editor.branches.v1.branch_deleted',
  true,
  (event: BranchDeletedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<BranchDeletedEnvelope>();
export const onBranchUpdated = EventDefinition(
  'wix.editor.branches.v1.branch_updated',
  true,
  (event: BranchUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
            { path: 'modifiedFields.createdDate' },
            { path: 'modifiedFields.updatedDate' },
          ],
        },
      ])
    )
)<BranchUpdatedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  ApplicationError,
  Asset,
  BaseEventMetadata,
  Branch,
  BranchCreatedEnvelope,
  BranchDeletedEnvelope,
  BranchQuerySpec,
  BranchSourceOneOf,
  BranchUpdatedEnvelope,
  BranchesQueryBuilder,
  BranchesQueryResult,
  BulkActionMetadata,
  BulkUpdateBranchTagsByFilterOptions,
  BulkUpdateBranchTagsByFilterRequest,
  BulkUpdateBranchTagsByFilterResponse,
  BulkUpdateBranchTagsOptions,
  BulkUpdateBranchTagsRequest,
  BulkUpdateBranchTagsResponse,
  BulkUpdateBranchTagsResult,
  CreateBranchRequest,
  CreateBranchResponse,
  CursorPaging,
  CursorPagingMetadata,
  CursorQuery,
  CursorQueryPagingMethodOneOf,
  Cursors,
  DeleteBranchRequest,
  DeleteBranchResponse,
  DeleteContext,
  DeleteStatus,
  DomainEvent,
  DomainEventBodyOneOf,
  EditorType,
  EditorTypeOptions,
  EditorlessAssigned,
  EditorlessUnassigned,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  ExtendedFields,
  GetBranchRequest,
  GetBranchResponse,
  GetDefaultBranchRequest,
  GetDefaultBranchResponse,
  HtmlSitePublished,
  IdentificationData,
  IdentificationDataIdOneOf,
  ItemMetadata,
  MessageEnvelope,
  MetaSiteSpecialEvent,
  MetaSiteSpecialEventPayloadOneOf,
  Namespace,
  NamespaceChanged,
  OdeditorAssigned,
  OdeditorUnassigned,
  Page,
  PicassoAssigned,
  PicassoUnassigned,
  PublicTags,
  QueryBranchesRequest,
  QueryBranchesResponse,
  RestoreInfo,
  ServiceProvisioned,
  ServiceRemoved,
  SetDefaultBranchRequest,
  SetDefaultBranchResponse,
  SiteCreated,
  SiteCreatedContext,
  SiteDeleted,
  SiteHardDeleted,
  SiteMarkedAsTemplate,
  SiteMarkedAsWixSite,
  SitePublished,
  SitePurgedExternally,
  SiteRenamed,
  SiteTransferred,
  SiteUndeleted,
  SiteUnpublished,
  SiteUrlChanged,
  SortOrder,
  Sorting,
  SourceBranch,
  SourceTemplate,
  SourceType,
  State,
  StudioAssigned,
  StudioTwoAssigned,
  StudioTwoUnassigned,
  StudioUnassigned,
  TagList,
  TagsModified,
  Type,
  UpdateBranch,
  UpdateBranchRequest,
  UpdateBranchResponse,
  UpdateExtendedFieldsRequest,
  UpdateExtendedFieldsResponse,
  UserDomainMediaDisabled,
  UserDomainMediaEnabled,
  WebhookIdentityType,
  WixelAssigned,
  WixelUnassigned,
  utils,
} from './editor-branches-v1-branch-branches.universal.js';
